import { useIsLocked360View } from "@modules/networth/hooks";
import { useTasksOpenCount } from "@modules/tasks/queries";
import {
  HiOutlineCalculator,
  HiOutlineCash,
  HiOutlineChartPie,
  HiOutlineChartSquareBar,
  HiOutlineClipboardCheck,
  HiOutlineDocumentText,
  HiOutlineLibrary,
  HiOutlineReceiptTax,
  HiOutlineScale,
  HiOutlineShieldCheck,
  HiOutlineTrendingUp,
  HiOutlineUsers,
} from "react-icons/hi";

import Image from "next/image";
import pofLogo from "@logos/pof-logo-small.png";
import { ClientRoutes } from "@common/constants";
import { useHousehold, useHouseholdAdvisors } from "@common/queries";
import { useCurrentAdvisor } from "@modules/advisor/queries";
import useAdvisorStore from "@modules/advisor/state";
import { isRestrictedAdvisor } from "@modules/advisor/utils";
import useStore from "@common/state";
import { useMemo } from "react";
import {
  useIsCobranded,
  usePoFExperience,
  useTaxClientPortal,
} from "@common/hooks";
import { useServiceLinePages } from "@modules/service-line-pages/hooks";
import { ClientSidebarNav } from "@common/types";

const useClientNavForWeb = () => {
  const { impersonateClientUserId } = useAdvisorStore();
  const { currentSpendingView, isSidebarExpanded } = useStore();
  const { data: household } = useHousehold();
  const { data: currentAdvisor } = useCurrentAdvisor({
    enabled: !!impersonateClientUserId,
  });
  const isAdvisorBlocked =
    !!impersonateClientUserId && isRestrictedAdvisor(currentAdvisor);
  const { isLocked360View } = useIsLocked360View({
    enabled: !isAdvisorBlocked,
  });
  const { isLockedInvestments, isLockedFinancialPlan } = household ?? {};
  const { data: tasksCount } = useTasksOpenCount();
  const { data: advisors } = useHouseholdAdvisors();

  const visibleAdvisors = advisors?.filter(
    (advisor) => !advisor.isHiddenFromClientPortal
  );

  const { isPof } = usePoFExperience();
  const { isCobranded, isCobrandedClient } = useIsCobranded();
  const { isTaxClientPortalEnabled } = useTaxClientPortal();
  const { isServiceLinePagesEnabled } = useServiceLinePages();

  const view360 = useMemo(
    () => ({
      href: ClientRoutes.Overview,
      icon: <HiOutlineChartPie />,
      text: isCobranded && !isCobrandedClient ? "Dashboard" : "360° View",
      isLocked: !isCobranded && isLocked360View,
    }),
    [isLocked360View, isCobranded, isCobrandedClient]
  );
  const financialPlan = useMemo(
    () => ({
      href: ClientRoutes.FinancialPlan,
      icon: <HiOutlineChartSquareBar />,
      text: "Financial Plan",
      isLocked: isLockedFinancialPlan || (isCobranded && !isCobrandedClient),
    }),
    [isLockedFinancialPlan, isCobranded, isCobrandedClient]
  );
  const documentVault = useMemo(
    () => ({
      href: ClientRoutes.DocumentVault,
      icon: <HiOutlineDocumentText />,
      text: "Document Vault",
      isLocked: false,
    }),
    []
  );
  const advisoryTeam = useMemo(
    () => ({
      href: ClientRoutes.AdvisoryTeam,
      icon: <HiOutlineUsers />,
      text: "Advisory Team",
      isLocked: !visibleAdvisors || visibleAdvisors.length === 0,
    }),
    [visibleAdvisors]
  );
  const tasksAndActions = useMemo(
    () => ({
      href: ClientRoutes.TaskAndActions,
      icon: <HiOutlineClipboardCheck />,
      text: "Tasks & Actions",
      badgeCount: tasksCount?.count,
      isLocked: isCobranded && !isCobrandedClient,
    }),
    [tasksCount?.count, isCobranded, isCobrandedClient]
  );
  const managedInvestments = useMemo(
    () => ({
      href: ClientRoutes.Investments,
      icon: <HiOutlineTrendingUp />,
      text: "Managed by Earned",
      isLocked: isLockedInvestments || (isCobranded && !isCobrandedClient),
    }),
    [isLockedInvestments, isCobranded, isCobrandedClient]
  );
  const accounts = useMemo(
    () => ({
      href: ClientRoutes.Accounts,
      icon: <HiOutlineLibrary />,
      text: "Outside Accounts",
      confirmMessage: impersonateClientUserId
        ? "Caution: Accessing this page may activate Multi-Factor Authentication (MFA) text messages and push notifications for the client. You can check account balances in the 360 View and manage the client's connected accounts using the 'Manage MX' button in the advisor impersonation portal. Do you wish to proceed?"
        : undefined,
    }),
    [impersonateClientUserId]
  );
  const spending = useMemo(
    () => ({
      href: ClientRoutes.Spending,
      query: { view: currentSpendingView },
      icon: <HiOutlineCash />,
      text: "Spending",
    }),
    [currentSpendingView]
  );
  const budgets = useMemo(
    () => ({
      href: ClientRoutes.Budgets,
      icon: <HiOutlineCalculator />,
      text: "Budgets",
    }),
    []
  );
  const taxCenter = useMemo(
    () => ({
      href: ClientRoutes.TaxCenter,
      icon: <HiOutlineReceiptTax />,
      text: "Tax Center",
    }),
    []
  );
  const tax = useMemo(
    () => ({
      href: "/service-lines/tax",
      icon: <HiOutlineReceiptTax />,
      text: "Tax",
    }),
    []
  );
  const legal = useMemo(
    () => ({
      href: "/service-lines/legal",
      icon: <HiOutlineScale />,
      text: "Legal",
    }),
    []
  );
  const insurance = useMemo(
    () => ({
      href: "/service-lines/insurance",
      icon: <HiOutlineShieldCheck />,
      text: "Insurance",
    }),
    []
  );

  const clientSidebar: ClientSidebarNav[] = useMemo(
    () => [
      {
        sectionTitle: "Home",
        containerClassName: `${
          isSidebarExpanded
            ? "sidebar-section-text-expanded"
            : "sidebar-section-text-collapsed"
        }`,
        links: [
          view360,
          ...(isPof && isCobrandedClient
            ? [
                {
                  href: ClientRoutes.PhysicianOnFire,
                  icon: (
                    <Image
                      src={pofLogo}
                      alt="Physician on FIRE logo"
                      width={16}
                      height={16}
                      style={{ objectFit: "contain" }}
                    />
                  ),
                  text: "Physician on FIRE",
                },
              ]
            : []),
          documentVault,
          tasksAndActions,
          advisoryTeam,
          ...(isTaxClientPortalEnabled ? [taxCenter] : []),
        ],
      },
      {
        sectionTitle: "Wealth",
        containerClassName: `whitespace-nowrap ${
          isSidebarExpanded
            ? "sidebar-section-text-expanded"
            : "sidebar-section-text-collapsed"
        }`,
        links: [financialPlan, managedInvestments, accounts, spending],
      },
      ...(isServiceLinePagesEnabled
        ? [
            {
              sectionTitle: "Additional Services",
              containerClassName: `whitespace-nowrap ${
                isSidebarExpanded
                  ? "sidebar-section-text-expanded"
                  : "sidebar-section-text-collapsed"
              }`,
              links: [tax, legal, insurance],
            },
          ]
        : []),
    ],
    [
      accounts,
      advisoryTeam,
      documentVault,
      financialPlan,
      insurance,
      isCobrandedClient,
      isPof,
      isServiceLinePagesEnabled,
      isSidebarExpanded,
      isTaxClientPortalEnabled,
      legal,
      managedInvestments,
      spending,
      tax,
      taxCenter,
      tasksAndActions,
      view360,
    ]
  );

  const cobrandedSidebar: ClientSidebarNav[] = useMemo(
    () => [
      {
        sectionTitle: "Your Money",
        containerClassName: `${
          isSidebarExpanded
            ? "sidebar-section-text-expanded"
            : "sidebar-section-text-collapsed"
        }`,
        links: [
          view360,
          ...(isPof && isCobrandedClient
            ? [
                {
                  href: ClientRoutes.PhysicianOnFire,
                  icon: (
                    <Image
                      src={pofLogo}
                      alt="Physician on FIRE logo"
                      width={16}
                      height={16}
                      style={{ objectFit: "contain" }}
                    />
                  ),
                  text: "Physician on FIRE",
                },
              ]
            : []),
          spending,
          budgets,
          ...(isTaxClientPortalEnabled ? [taxCenter] : []),
        ],
      },
      {
        sectionTitle: "Wealth Management",
        containerClassName: `whitespace-nowrap ${
          isSidebarExpanded
            ? "sidebar-section-text-expanded"
            : "sidebar-section-text-collapsed"
        }`,
        links: [
          managedInvestments,
          documentVault,
          tasksAndActions,
          advisoryTeam,
          financialPlan,
        ],
      },
    ],
    [
      advisoryTeam,
      budgets,
      documentVault,
      financialPlan,
      isCobrandedClient,
      isPof,
      isSidebarExpanded,
      isTaxClientPortalEnabled,
      managedInvestments,
      spending,
      taxCenter,
      tasksAndActions,
      view360,
    ]
  );

  if (isCobranded && !isCobrandedClient) {
    return cobrandedSidebar;
  }

  return clientSidebar;
};

export default useClientNavForWeb;
